import React, { forwardRef, useEffect, useState, Fragment } from 'react';
import CIcon from '@coreui/icons-react';
import DOMPurify from 'dompurify';
import { escapeRegExp } from '../../../utils';
import PropTypes from 'prop-types';

const CustomDropdownItem = forwardRef((props, ref) => {
     const {
          listDropdownItem,
          itemActive,
          handleClickListDropdownItem,
          search,
          hasSections = true,
     } = props;

     const [searchDropDownItem, setSearchDropdownItem] = useState('');
     const [filterListDropdownItem, setFilterListDropdownItem] = useState(listDropdownItem.filter((item) => !itemActive.includes(item.label)));
     const [openSections, setOpenSections] = useState({});

     // Extract sections from items (similar to SelectOption)
     const getSections = (items) => {
          if (!hasSections || !Array.isArray(items)) return [];

          const sections = Array.from(new Set(items.map(item => {
               if (typeof item === 'object' && item.sectionParent) {
                    return item.sectionParent;
               } else if (typeof item === 'object' && item.section) {
                    return item.section;
               }
               return 'Select All';
          })));

          return sections.sort((a, b) => {
               if (a === 'Select All' && b !== 'Select All') return 1;
               if (b === 'Select All' && a !== 'Select All') return -1;
               return 0;
          });
     };

     const getItemsBySection = (items, section) => {
          if (!hasSections) return items;
          return items.filter(item => {
               if (typeof item === 'object' && item.section) {
                    return item.section === section;
               }
               return section === 'Select All';
          });
     };

     const getChildSections = (items, parentSection) => {
          if (!hasSections) return [];
          const childItems = items.filter(item =>
               typeof item === 'object' && item.sectionParent === parentSection
          );
          return Array.from(new Set(childItems.map(item => item.section)));
     };

     const getItemLabel = (item) => {
          if (typeof item === 'object' && item.label) {
               return item.label;
          }
          return item;
     };

     const getItemValue = (item) => {
          if (typeof item === 'object' && item.value) {
               return item.value;
          }
          return item;
     };

     const isItemActive = (item) => {
          if (!itemActive || !Array.isArray(itemActive)) return false;
          return itemActive.some(activeItem => {
               if (typeof activeItem === 'string') {
                    return activeItem === getItemLabel(item);
               }
               return activeItem.label === getItemLabel(item);
          });
     };

     function filterItemsBySearch(items, searchInput, currentList) {
          const filteredItems = items.filter((item) => {
               const label = getItemLabel(item);
               const lowercaseLabel = label.toLowerCase();
               const isExcluded = currentList.some((excludedLabel) => {
                    const excludedLabelLower = typeof excludedLabel === 'string' ? excludedLabel.toLowerCase() : getItemLabel(excludedLabel).toLowerCase();
                    return lowercaseLabel === excludedLabelLower;
               });
               return lowercaseLabel.includes(searchInput.toLowerCase()) && !isExcluded;
          });
          return filteredItems;
     }

     const handleSectionToggle = (section) => {
          if (section === 'Select All') {
               // Handle select all logic if needed
               handleClickListDropdownItem(listDropdownItem);
          } else {
               setOpenSections(prev => ({
                    ...prev,
                    [section]: !prev[section]
               }));
          }
     };

     const handleItemClick = (item) => {
          handleClickListDropdownItem(item.label);
     };

     const handleSearch = (searchValue) => {
          setSearchDropdownItem(searchValue);
          const filteredItems = filterItemsBySearch(listDropdownItem, searchValue, itemActive);
          setFilterListDropdownItem(filteredItems);

          // Auto-open sections that have matching items
          const sections = getSections(filteredItems);
          const openSectionsObj = {};
          sections.forEach(section => {
               const sectionItems = getItemsBySection(filteredItems, section);
               if (sectionItems.length > 0) {
                    openSectionsObj[section] = true;
               }
          });
          setOpenSections(openSectionsObj);
     };


     useEffect(() => {
          setFilterListDropdownItem(listDropdownItem.filter((item) => !itemActive.includes(item.label)));
     }, [listDropdownItem, itemActive]);

     const sections = getSections(filterListDropdownItem);
     const noOptionsFound = sections.every(section => {
          const sectionItems = getItemsBySection(filterListDropdownItem, section);
          const childSections = getChildSections(filterListDropdownItem, section);
          return sectionItems.length === 0 && childSections.length === 0;
     });

     return (
          <div ref={ref} className='dropdownMenuItem'>
               {search && <input type='text' placeholder='Select or type to search' value={searchDropDownItem} onChange={(event) => handleSearch(event.target.value)} />}
               {hasSections ? (
                    <div className="insert-variable-dropdown">
                         <ul className={filterListDropdownItem.length === 0 ? 'value-no-options-available no-options' : ''}>
                              {noOptionsFound ? (
                                   <li className="no-options border-bottom d-flex align-items-center justify-content-center text-center">
                                        No options
                                   </li>
                              ) : (
                                   sections.map((section, sectionIndex) => {
                                        const sectionItems = getItemsBySection(filterListDropdownItem, section);
                                        const childSections = getChildSections(filterListDropdownItem, section);
                                        const isOpen = openSections[section];

                                        if (sectionItems.length === 0 && childSections.length === 0) return null;

                                        return (
                                             <Fragment key={sectionIndex}>
                                                  <li
                                                       className="d-flex align-items-center justify-content-between border-bottom border-top type-variable"
                                                       onClick={() => handleSectionToggle(section)}
                                                       style={{ cursor: 'pointer', padding: '8px 12px' }}
                                                  >
                                                       <strong className="mr-1">{section}</strong>
                                                       {isOpen ? (
                                                            <strong style={{ pointerEvents: 'none' }}>
                                                                 {childSections.length > 0 ? (
                                                                      childSections.length
                                                                 ) : (
                                                                      sectionItems.length
                                                                 )}
                                                            </strong>
                                                       ) : (
                                                            section !== 'Select All' ? (
                                                                 <CIcon
                                                                      style={{ pointerEvents: 'none' }}
                                                                      icon="iconArrowDownStrong"
                                                                 />
                                                            ) : null
                                                       )}
                                                  </li>

                                                  {isOpen && (
                                                       <>
                                                            {/* Child sections */}
                                                            {childSections.map((childSection, childIndex) => {
                                                                 const childItems = filterListDropdownItem.filter(item =>
                                                                      typeof item === 'object' &&
                                                                      item.section === childSection &&
                                                                      item.sectionParent === section
                                                                 );

                                                                 return (
                                                                      <Fragment key={childIndex}>
                                                                           <li
                                                                                className="d-flex align-items-center justify-content-between border-bottom border-top type-variable child"
                                                                                onClick={() => handleSectionToggle(`${section}_${childSection}`)}
                                                                                style={{ cursor: 'pointer', padding: '8px 24px' }}
                                                                           >
                                                                                <strong className="mr-1">{childSection}</strong>
                                                                                {openSections[`${section}_${childSection}`] ? (
                                                                                     <strong style={{ pointerEvents: 'none' }}>
                                                                                          {childItems.length}
                                                                                     </strong>
                                                                                ) : (
                                                                                     <CIcon
                                                                                          style={{ pointerEvents: 'none' }}
                                                                                          icon="iconArrowDownStrong"
                                                                                     />
                                                                                )}
                                                                           </li>

                                                                           {openSections[`${section}_${childSection}`] && (
                                                                                <ul>
                                                                                     {childItems.map((item, itemIndex) => {
                                                                                          const activeValue = isItemActive(item);
                                                                                          const re = new RegExp(escapeRegExp(searchDropDownItem), 'gi');
                                                                                          let highlightedLabel = getItemLabel(item);
                                                                                          if (searchDropDownItem) {
                                                                                               highlightedLabel = highlightedLabel.replace(re, (match) => `<strong>${match}</strong>`);
                                                                                          }
                                                                                          const sanitizedLabel = DOMPurify.sanitize(highlightedLabel);

                                                                                          return (
                                                                                               <li
                                                                                                    key={getItemValue(item) + itemIndex}
                                                                                                    className={`border-bottom d-flex align-items-center ${activeValue ? 'active-variable' : ''}`}
                                                                                                    onClick={() => handleItemClick(item)}
                                                                                                    style={{ cursor: 'pointer', padding: '8px 36px' }}
                                                                                               >
                                                                                                    <span
                                                                                                         className="text-break"
                                                                                                         dangerouslySetInnerHTML={{ __html: sanitizedLabel }}
                                                                                                    />
                                                                                               </li>
                                                                                          );
                                                                                     })}
                                                                                </ul>
                                                                           )}
                                                                      </Fragment>
                                                                 );
                                                            })}

                                                            {/* Main section items */}
                                                            {sectionItems.length > 0 && (
                                                                 <ul>
                                                                      {sectionItems.map((item, itemIndex) => {
                                                                           const activeValue = isItemActive(item);
                                                                           const re = new RegExp(escapeRegExp(searchDropDownItem), 'gi');
                                                                           let highlightedLabel = getItemLabel(item);
                                                                           if (searchDropDownItem) {
                                                                                highlightedLabel = highlightedLabel.replace(re, (match) => `<strong>${match}</strong>`);
                                                                           }
                                                                           const sanitizedLabel = DOMPurify.sanitize(highlightedLabel);

                                                                           return (
                                                                                <li
                                                                                     key={getItemValue(item) + itemIndex}
                                                                                     className={`border-bottom d-flex align-items-center ${activeValue ? 'active-variable' : ''}`}
                                                                                     onClick={() => handleItemClick(item)}
                                                                                     style={{ cursor: 'pointer', padding: '8px 24px' }}
                                                                                >
                                                                                     <span
                                                                                          className="text-break"
                                                                                          dangerouslySetInnerHTML={{ __html: sanitizedLabel }}
                                                                                     />
                                                                                </li>
                                                                           );
                                                                      })}
                                                                 </ul>
                                                            )}
                                                       </>
                                                  )}
                                             </Fragment>
                                        );
                                   })
                              )}
                         </ul>
                    </div>
               ) : (
                    <ul>
                         {filterListDropdownItem?.map((item, index) => (
                                   <li
                                        key={getItemValue(item) + index}
                                        onClick={() => handleItemClick(item)}
                                        className={isItemActive(item) ? 'active-variable' : ''}
                                        style={{ cursor: 'pointer', padding: '8px 12px' }}
                                   >
                                        {getItemLabel(item)}
                                   </li>
                              ))}
                    </ul>
               )}


          </div>
     );
});


CustomDropdownItem.propTypes = {
     listDropdownItem: PropTypes.array,
     itemActive: PropTypes.any,
     handleClickListDropdownItem: PropTypes.func,
     search: PropTypes.any,
     hasSections: PropTypes.bool,
};

export default CustomDropdownItem;
